import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import CloudIcon from '@mui/icons-material/Cloud';
import DownloadIcon from '@mui/icons-material/Download';

import { AppContext } from '../context/AppContext';
import { getBooks } from '../utils/books';
import { useTranslation } from '../utils/i18n';
import {
  dismissAndroidPromotion,
  isAndroidPromotionDismissed,
  isAndroidWebRuntime,
  openAndroidAppPromotion,
} from '../utils/androidPromotion';
import {
  createBackupZip,
  initialBackupProgressState,
  type BackupProgressState,
} from '../utils/backupExport';
import {
  getGoogleDriveStatus,
  isGoogleDriveReauthorizationRequiredError,
  startGoogleDriveConnect,
  syncGoogleDriveNow,
  type DriveSyncStatus,
} from '../utils/driveSync';
import { logger } from '../utils/logger';
import BackupProgressDialog from './BackupProgressDialog';

const AndroidAppPromotionPrompt: React.FC = () => {
  const t = useTranslation();
  const { isOnline, localForageLoaded, setSnackbarMessage, triggerRefresh, user } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [driveStatus, setDriveStatus] = useState<DriveSyncStatus | null>(null);
  const [driveStatusLoaded, setDriveStatusLoaded] = useState(false);
  const [busyAction, setBusyAction] = useState<'drive' | 'backup' | 'store' | null>(null);
  const [driveCompleted, setDriveCompleted] = useState(false);
  const [backupCompleted, setBackupCompleted] = useState(false);
  const [backupProgress, setBackupProgress] = useState<BackupProgressState>(initialBackupProgressState);

  const localBookCount = useMemo(() => {
    if (!localForageLoaded || !user?.email) {
      return 0;
    }

    return Object.values(getBooks()).filter((book) => book.location !== 'trash').length;
  }, [localForageLoaded, user?.email]);
  const hasLocalBooks = localBookCount > 0;
  const driveAvailable = Boolean(isOnline && (!driveStatusLoaded || (driveStatus && driveStatus.configured !== false)));
  const shouldPreferDrive = hasLocalBooks && driveAvailable && !driveCompleted;
  const shouldPreferBackup = hasLocalBooks && !driveAvailable && !driveCompleted && !backupCompleted;

  useEffect(() => {
    if (!localForageLoaded || !user?.email || !isAndroidWebRuntime()) {
      setOpen(false);
      return;
    }

    if (isAndroidPromotionDismissed(user.email)) {
      setOpen(false);
      return;
    }

    setOpen(true);
    logger.info('Presenting Android app promotion prompt', {
      hasLocalBooks,
      localBookCount,
      isOnline,
    });
  }, [hasLocalBooks, isOnline, localBookCount, localForageLoaded, user?.email]);

  useEffect(() => {
    if (!open || !user?.email || !isOnline) {
      setDriveStatusLoaded(!isOnline);
      setDriveStatus(null);
      return;
    }

    let cancelled = false;
    setDriveStatusLoaded(false);
    void (async () => {
      try {
        const status = await getGoogleDriveStatus();
        if (!cancelled) {
          setDriveStatus(status);
        }
      } catch (error) {
        logger.warn('Android app promotion Drive status check failed', {
          error: error instanceof Error ? error.message : String(error),
        });
        if (!cancelled) {
          setDriveStatus(null);
        }
      } finally {
        if (!cancelled) {
          setDriveStatusLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOnline, open, user?.email]);

  const dismissPrompt = () => {
    dismissAndroidPromotion(user?.email);
    setOpen(false);
  };

  const handleOpenStore = async () => {
    setBusyAction('store');
    dismissAndroidPromotion(user?.email);
    try {
      await openAndroidAppPromotion(
        hasLocalBooks ? (driveCompleted ? 'logged_in_prompt_after_drive' : 'logged_in_prompt_library') : 'logged_in_prompt_empty',
      );
      setOpen(false);
    } finally {
      setBusyAction(null);
    }
  };

  const handleDriveSync = async () => {
    if (!driveStatusLoaded) {
      return;
    }

    setBusyAction('drive');
    try {
      const status = driveStatus || (await getGoogleDriveStatus());
      setDriveStatus(status);

      if (!status.connected) {
        setSnackbarMessage({ text: t('DRIVE_SYNC_OPENING_CONSENT'), duration: 4000 });
        await startGoogleDriveConnect();
        dismissAndroidPromotion(user?.email);
        setOpen(false);
        return;
      }

      setSnackbarMessage({ text: t('DRIVE_SYNC_STARTED'), duration: null });
      await syncGoogleDriveNow('android-app-promotion');
      triggerRefresh();
      setDriveCompleted(true);
      setSnackbarMessage({ text: t('ANDROID_APP_PROMPT_DRIVE_COMPLETE'), duration: 6000 });
      setDriveStatus(await getGoogleDriveStatus());
    } catch (error) {
      if (isGoogleDriveReauthorizationRequiredError(error)) {
        logger.warn('Android app promotion Drive sync requires reauthorization', {
          error: error instanceof Error ? error.message : String(error),
        });
        setSnackbarMessage({ text: t('DRIVE_SYNC_OPENING_CONSENT'), duration: 4000 });
        await startGoogleDriveConnect();
        dismissAndroidPromotion(user?.email);
        setOpen(false);
        return;
      }

      logger.error('Android app promotion Drive sync failed', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      setSnackbarMessage({ text: t('DRIVE_SYNC_FAILED'), duration: 6000 });
    } finally {
      setBusyAction(null);
    }
  };

  const handleBackup = async () => {
    setBusyAction('backup');
    const result = await createBackupZip({
      t,
      setSnackbarMessage,
      onProgress: (nextState) => {
        setBackupProgress((previous) => ({
          ...previous,
          ...nextState,
        }));
      },
    });
    setBackupProgress(initialBackupProgressState);
    if (result.succeeded) {
      setBackupCompleted(true);
      setSnackbarMessage({ text: t('ANDROID_APP_PROMPT_BACKUP_READY'), duration: 6000 });
    }
    setBusyAction(null);
  };

  if (!open) {
    return null;
  }

  const primaryAction = shouldPreferDrive ? handleDriveSync : shouldPreferBackup ? handleBackup : handleOpenStore;
  const primaryLabel = shouldPreferDrive
    ? driveStatus?.connected
      ? t('ANDROID_APP_PROMPT_SYNC_DRIVE')
      : t('ANDROID_APP_PROMPT_CONNECT_DRIVE')
    : shouldPreferBackup
      ? t('ANDROID_APP_PROMPT_BACKUP')
    : t('ANDROID_APP_PROMPT_OPEN_STORE');
  const primaryIcon = shouldPreferDrive ? <CloudIcon /> : shouldPreferBackup ? <DownloadIcon /> : <AndroidIcon />;
  const body = hasLocalBooks
    ? driveCompleted
      ? t('ANDROID_APP_PROMPT_BODY_READY')
      : backupCompleted
        ? t('ANDROID_APP_PROMPT_BODY_BACKUP_READY')
      : driveAvailable
        ? t('ANDROID_APP_PROMPT_BODY_LIBRARY_DRIVE')
        : t('ANDROID_APP_PROMPT_BODY_LIBRARY_BACKUP')
    : t('ANDROID_APP_PROMPT_BODY_EMPTY');

  return (
    <>
      <Dialog open={open} onClose={dismissPrompt} aria-labelledby='android-app-prompt-title' maxWidth='xs' fullWidth>
        <DialogTitle id='android-app-prompt-title'>{t('ANDROID_APP_PROMPT_TITLE')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Stack direction='row' spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={dismissPrompt}>{t('ANDROID_APP_PROMPT_NOT_NOW')}</Button>
            {hasLocalBooks && !shouldPreferBackup && (
              <Button
                onClick={handleBackup}
                disabled={busyAction !== null || backupProgress.open}
                startIcon={busyAction === 'backup' ? <CircularProgress size={16} /> : <DownloadIcon />}
              >
                {t('ANDROID_APP_PROMPT_BACKUP')}
              </Button>
            )}
            {hasLocalBooks && shouldPreferBackup && (
              <Button onClick={handleOpenStore} disabled={busyAction !== null} startIcon={<AndroidIcon />}>
                {t('ANDROID_APP_PROMPT_OPEN_STORE')}
              </Button>
            )}
            <Button
              variant='contained'
              onClick={primaryAction}
              disabled={busyAction !== null || (shouldPreferDrive && !driveStatusLoaded)}
              startIcon={
                busyAction === (shouldPreferDrive ? 'drive' : shouldPreferBackup ? 'backup' : 'store') ? (
                  <CircularProgress size={16} />
                ) : (
                  primaryIcon
                )
              }
            >
              {primaryLabel}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <BackupProgressDialog progress={backupProgress} t={t} />
    </>
  );
};

export default AndroidAppPromotionPrompt;
